import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import { ArrowLeft, Zap, Loader2, Check, X, Sparkles, RefreshCw, Trash2, ExternalLink, Plus, Upload, DollarSign, BarChart3 } from "lucide-react";

/**
 * Fine-Tune Bridge — send a training job to Fireworks / Together and auto-register the result.
 */
export default function StudioFineTune() {
  const { jobId } = useParams();
  const nav = useNavigate();

  const [presets, setPresets] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(!jobId);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);  // {file_id, size_bytes}
  const [datasetTokens, setDatasetTokens] = useState(500_000);
  const pollRef = useRef(null);

  const [form, setForm] = useState({
    provider: "fireworks",
    api_key: "",
    account_id: "",
    base_model: "accounts/fireworks/models/llama-v3p1-8b-instruct",
    dataset_source: "ref",  // "ref" (native) | "url" | "hf"
    dataset_ref: "",
    dataset_hf_id: "yahma/alpaca-cleaned",
    dataset_url: "",
    suffix: "aimedley",
    epochs: 1,
    learning_rate: 0.0001,
    lora_rank: 8,
  });

  const loadPresets = useCallback(() => {
    api.get("/custom_brains/presets").then(({ data }) => setPresets(data.presets || []));
  }, []);

  const loadPricing = useCallback(() => {
    api.get("/fine_tune/pricing").then(({ data }) => setPricing(data.pricing || null));
  }, []);

  const loadJobs = useCallback(() => {
    api.get("/fine_tune/jobs").then(({ data }) => setJobs(data.jobs || []));
  }, []);

  useEffect(() => {
    loadPresets();
    loadPricing();
    loadJobs();
  }, [loadPresets, loadPricing, loadJobs]);

  // Load specific job detail + start polling if running
  const loadJobDetail = useCallback(async (id) => {
    try {
      const r = await api.get(`/fine_tune/jobs/${id}`);
      setDetail(r.data);
      return r.data;
    } catch (e) {
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    if (!jobId) {
      setDetail(null);
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    setShowForm(false);
    loadJobDetail(jobId);
    // Poll every 15s
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await loadJobDetail(jobId);
      if (d && (d.status === "completed" || d.status === "failed")) {
        clearInterval(pollRef.current);
        loadJobs();
      }
    }, 15000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [jobId, loadJobDetail, loadJobs]);

  const currentPreset = presets.find((p) => p.id === form.provider);
  const ftPresets = presets.filter((p) => p.supports_fine_tune);

  // Cost estimator — coarse USD estimate based on provider pricing per 1M tokens
  const estimatedCost = useMemo(() => {
    if (!pricing) return null;
    const p = pricing[form.provider];
    if (!p) return null;
    const bm = (form.base_model || "").toLowerCase();
    let rate = p.default;
    for (const key of Object.keys(p)) {
      if (key !== "default" && bm.includes(key)) { rate = p[key]; break; }
    }
    const totalTokens = (parseInt(datasetTokens) || 0) * (parseInt(form.epochs) || 1);
    const cost = (totalTokens / 1_000_000) * rate;
    return { rate, cost, totalTokens };
  }, [pricing, form.provider, form.base_model, form.epochs, datasetTokens]);

  const uploadTogether = async (fileInputEvent) => {
    const file = fileInputEvent.target.files?.[0];
    if (!file) return;
    if (!form.api_key.trim()) {
      alert("Paste your Together API key above first — it's needed to upload.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("api_key", form.api_key.trim());
      fd.append("file", file);
      const r = await api.post("/fine_tune/together/upload-dataset", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedFile({ file_id: r.data.file_id, size_bytes: r.data.size_bytes, name: file.name });
      // Auto-fill URL field with the file-id (Together accepts file-ids for training_file)
      setForm((f) => ({ ...f, dataset_source: "url", dataset_url: r.data.file_id }));
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      fileInputEvent.target.value = "";
    }
  };

  const createJob = async () => {
    setCreating(true);
    try {
      const payload = {
        provider: form.provider,
        api_key: form.api_key.trim(),
        base_model: form.base_model.trim(),
        suffix: form.suffix.trim(),
        epochs: parseInt(form.epochs),
        learning_rate: parseFloat(form.learning_rate),
        lora_rank: parseInt(form.lora_rank),
      };
      if (form.provider === "fireworks") payload.account_id = form.account_id.trim();
      if (form.dataset_source === "hf") payload.dataset_hf_id = form.dataset_hf_id.trim();
      else if (form.dataset_source === "url") payload.dataset_url = form.dataset_url.trim();
      else payload.dataset_ref = form.dataset_ref.trim();

      const r = await api.post("/fine_tune/jobs", payload);
      loadJobs();
      nav(`/studio/fine-tune/${r.data.job_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  const register = async () => {
    setRegistering(true);
    try {
      const r = await api.post(`/fine_tune/jobs/${detail.job_id}/register`);
      loadJobs();
      await loadJobDetail(detail.job_id);
      nav(`/studio`, { state: { toast: `Fine-tuned model saved as custom brain ${r.data.brain_id}` } });
    } catch (e) {
      alert(e.response?.data?.detail || "Registration failed");
    } finally {
      setRegistering(false);
    }
  };

  const cancel = async (id) => {
    if (!confirm("Delete this job record? (This only removes it locally.)")) return;
    await api.delete(`/fine_tune/jobs/${id}`);
    loadJobs();
    if (jobId === id) nav("/studio/fine-tune");
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="ft-back">
          <ArrowLeft size={14} strokeWidth={2.5} /> Studio
        </Link>
      </div>
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <Zap size={12} strokeWidth={2.5} /> Fine-Tune Bridge
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="ft-title">Ship a fine-tune, ship a brain</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Send a supervised fine-tune to Fireworks or Together AI, watch it train, then click one button to register the output as a Custom Brain — usable everywhere on AiMedley.
        </p>
      </div>

      {/* Detail view */}
      {jobId && detail && <JobDetail job={detail} onRegister={register} registering={registering} onRefresh={() => loadJobDetail(jobId)} onDelete={() => cancel(jobId)} />}

      {/* Empty state or list */}
      {!jobId && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2">
              <Zap size={18} strokeWidth={2.5} /> Your fine-tune jobs
              <span className="text-xs bg-slate-100 border-2 border-slate-900 rounded-full px-2 py-0.5 font-mono">{jobs.length}</span>
            </h3>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="brut-btn text-sm"
              data-testid="ft-toggle-form"
            >
              <Plus size={12} strokeWidth={3} /> {showForm ? "Hide form" : "New job"}
            </button>
          </div>

          {showForm && (
            <div className="brut-card p-5 space-y-4" data-testid="ft-form">
              <div>
                <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">Provider</div>
                <div className="flex flex-wrap gap-2">
                  {ftPresets.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setForm((f) => ({
                        ...f,
                        provider: p.id,
                        dataset_source: p.id === "fireworks" ? "ref" : "hf",
                        base_model: p.id === "fireworks"
                          ? "accounts/fireworks/models/llama-v3p1-8b-instruct"
                          : "meta-llama/Meta-Llama-3.1-8B-Instruct-Reference",
                      }))}
                      className={`brut-btn text-sm ${form.provider === p.id ? "" : "bg-white text-slate-900"}`}
                      data-testid={`ft-provider-${p.id}`}
                    >
                      <span className="text-base">{p.avatar}</span> {p.name}
                    </button>
                  ))}
                </div>
                {currentPreset?.key_url && (
                  <p className="text-xs text-slate-500 mt-1">
                    Get an API key at{" "}
                    <a className="underline" href={currentPreset.key_url} target="_blank" rel="noreferrer">
                      {new URL(currentPreset.key_url).hostname} <ExternalLink size={9} className="inline" />
                    </a>
                  </p>
                )}
              </div>

              <Field label="API key" required>
                <input
                  type="password"
                  className="brut-input w-full font-mono"
                  placeholder={currentPreset?.key_prefix ? `${currentPreset.key_prefix}...` : "sk-..."}
                  value={form.api_key}
                  onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                  data-testid="ft-api-key"
                  autoComplete="off"
                />
              </Field>

              {form.provider === "fireworks" && (
                <Field label="Fireworks account ID" required>
                  <input
                    className="brut-input w-full font-mono"
                    placeholder="e.g. my-team-name (from fireworks.ai/account)"
                    value={form.account_id}
                    onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))}
                    data-testid="ft-account-id"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Find it at{" "}
                    <a className="underline" href="https://fireworks.ai/account" target="_blank" rel="noreferrer">
                      fireworks.ai/account
                    </a>
                    . Required to scope the fine-tune API call.
                  </p>
                </Field>
              )}

              <Field label="Base model to fine-tune" required>
                <input
                  className="brut-input w-full font-mono"
                  value={form.base_model}
                  onChange={(e) => setForm((f) => ({ ...f, base_model: e.target.value }))}
                  data-testid="ft-base-model"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {form.provider === "fireworks"
                    ? "e.g. accounts/fireworks/models/llama-v3p1-8b-instruct — must be fine-tunable on Fireworks."
                    : "e.g. meta-llama/Llama-3.1-8B-Instruct — must be fine-tunable on Together."}
                </p>
              </Field>

              <div>
                <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">Training dataset</div>
                <div className="inline-flex rounded-lg border-2 border-slate-900 overflow-hidden mb-2">
                  {form.provider === "fireworks" && (
                    <button
                      className={`text-xs px-3 py-1 font-heading font-bold uppercase tracking-widest ${form.dataset_source === "ref" ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
                      onClick={() => setForm((f) => ({ ...f, dataset_source: "ref" }))}
                      data-testid="ft-dataset-ref-btn"
                    >
                      Fireworks resource
                    </button>
                  )}
                  <button
                    className={`text-xs px-3 py-1 font-heading font-bold uppercase tracking-widest ${form.dataset_source === "url" ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
                    onClick={() => setForm((f) => ({ ...f, dataset_source: "url" }))}
                    data-testid="ft-dataset-url"
                  >
                    URL to JSONL
                  </button>
                  {form.provider === "together" && (
                    <button
                      className={`text-xs px-3 py-1 font-heading font-bold uppercase tracking-widest ${form.dataset_source === "hf" ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
                      onClick={() => setForm((f) => ({ ...f, dataset_source: "hf" }))}
                      data-testid="ft-dataset-hf"
                    >
                      HF dataset id
                    </button>
                  )}
                </div>
                {form.dataset_source === "ref" ? (
                  <>
                    <input
                      className="brut-input w-full font-mono"
                      placeholder={`accounts/${form.account_id || "<account_id>"}/datasets/my-dataset`}
                      value={form.dataset_ref}
                      onChange={(e) => setForm((f) => ({ ...f, dataset_ref: e.target.value }))}
                      data-testid="ft-dataset-ref-val"
                    />
                    <p className="text-xs text-amber-800 bg-amber-50 border-2 border-amber-300 rounded-lg p-2 mt-2">
                      Fireworks requires the dataset to already exist in your account. Upload it first with{" "}
                      <a className="underline font-bold" href="https://docs.fireworks.ai/tools-sdks/firectl/commands/create-dataset" target="_blank" rel="noreferrer">
                        <code>firectl create dataset</code>
                      </a>{" "}
                      or the{" "}
                      <a className="underline font-bold" href="https://docs.fireworks.ai/api-reference/create-dataset" target="_blank" rel="noreferrer">Datasets API</a>.
                    </p>
                  </>
                ) : form.dataset_source === "hf" ? (
                  <input
                    className="brut-input w-full font-mono"
                    placeholder="e.g. yahma/alpaca-cleaned"
                    value={form.dataset_hf_id}
                    onChange={(e) => setForm((f) => ({ ...f, dataset_hf_id: e.target.value }))}
                    data-testid="ft-dataset-hf-id"
                  />
                ) : (
                  <div className="space-y-2">
                    <input
                      className="brut-input w-full font-mono"
                      placeholder={form.provider === "together" ? "file-... (uploaded) or https://.../train.jsonl" : "https://example.com/train.jsonl"}
                      value={form.dataset_url}
                      onChange={(e) => setForm((f) => ({ ...f, dataset_url: e.target.value }))}
                      data-testid="ft-dataset-url-val"
                    />
                    {form.provider === "together" && (
                      <div className="flex items-center gap-2">
                        <label className={`brut-btn text-xs cursor-pointer ${uploading ? "opacity-60" : ""}`} data-testid="ft-together-upload-label">
                          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} strokeWidth={3} />}
                          {uploading ? "Uploading…" : "Upload JSONL to Together"}
                          <input
                            type="file"
                            accept=".jsonl,application/x-ndjson,application/json"
                            className="hidden"
                            disabled={uploading || !form.api_key.trim()}
                            onChange={uploadTogether}
                            data-testid="ft-together-upload-input"
                          />
                        </label>
                        {uploadedFile && (
                          <span className="text-xs bg-emerald-50 border-2 border-emerald-300 rounded-lg px-2 py-1 font-mono text-emerald-900 inline-flex items-center gap-1" data-testid="ft-uploaded-file">
                            <Check size={11} strokeWidth={3} /> {uploadedFile.name} → {uploadedFile.file_id}
                          </span>
                        )}
                        {!form.api_key.trim() && (
                          <span className="text-[10px] text-slate-500 italic">Paste API key above first</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Dataset must be in OpenAI chat format (JSONL with {"{"}messages: [...]{"}"}).
                </p>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <Field label="Suffix">
                  <input
                    className="brut-input w-full"
                    value={form.suffix}
                    onChange={(e) => setForm((f) => ({ ...f, suffix: e.target.value }))}
                    data-testid="ft-suffix"
                  />
                </Field>
                <Field label="Epochs">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="brut-input w-full"
                    value={form.epochs}
                    onChange={(e) => setForm((f) => ({ ...f, epochs: e.target.value }))}
                    data-testid="ft-epochs"
                  />
                </Field>
                <Field label="Learning rate">
                  <input
                    type="number"
                    step="0.0001"
                    className="brut-input w-full"
                    value={form.learning_rate}
                    onChange={(e) => setForm((f) => ({ ...f, learning_rate: e.target.value }))}
                    data-testid="ft-lr"
                  />
                </Field>
                <Field label="LoRA rank">
                  <input
                    type="number"
                    min="4"
                    max="64"
                    className="brut-input w-full"
                    value={form.lora_rank}
                    onChange={(e) => setForm((f) => ({ ...f, lora_rank: e.target.value }))}
                    data-testid="ft-lora"
                  />
                </Field>
              </div>

              {/* Cost Estimator */}
              {estimatedCost && (
                <div className="brut-card p-3 bg-emerald-50 border-emerald-300 space-y-2" data-testid="ft-cost-estimator">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-heading font-bold text-emerald-900">
                    <DollarSign size={12} strokeWidth={2.5} /> Cost estimator
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    <label className="inline-flex items-center gap-2">
                      <span className="text-xs text-slate-600">Dataset size (tokens)</span>
                      <input
                        type="number"
                        min="1000"
                        step="100000"
                        value={datasetTokens}
                        onChange={(e) => setDatasetTokens(parseInt(e.target.value) || 0)}
                        className="brut-input text-sm w-32 tabular-nums"
                        data-testid="ft-dataset-tokens"
                      />
                    </label>
                    <div className="text-sm text-slate-700 inline-flex items-center gap-1">
                      × <b>{form.epochs}</b> epochs = <b className="tabular-nums">{(estimatedCost.totalTokens / 1_000_000).toFixed(2)}M</b> training tokens
                    </div>
                  </div>
                  <div className="text-sm">
                    Estimated cost: <span className="font-heading text-2xl font-bold text-emerald-800 tabular-nums" data-testid="ft-cost-value">${estimatedCost.cost.toFixed(2)}</span>
                    <span className="text-xs text-slate-500 ml-2">(@ ${estimatedCost.rate.toFixed(2)} per 1M tokens on {currentPreset?.name})</span>
                  </div>
                  <p className="text-[10px] text-slate-500 italic">Rough estimate from public pricing. Real charges land on your {currentPreset?.name} dashboard.</p>
                </div>
              )}

              <button
                onClick={createJob}
                disabled={creating || !form.api_key.trim() || !form.base_model.trim() ||
                  (form.provider === "fireworks" && !form.account_id.trim()) ||
                  (form.dataset_source === "hf" ? !form.dataset_hf_id.trim() :
                   form.dataset_source === "url" ? !form.dataset_url.trim() :
                   !form.dataset_ref.trim())}
                className="brut-btn text-sm disabled:opacity-40"
                data-testid="ft-create"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={3} />} Launch training job
              </button>
              <p className="text-xs text-amber-800 bg-amber-50 border-2 border-amber-300 rounded-lg p-3">
                <b>Heads up:</b> Fine-tuning can take 15 min → several hours and will incur charges on your {currentPreset?.name} account.
              </p>
            </div>
          )}

          {jobs.length === 0 ? (
            <div className="brut-card p-8 text-center">
              <div className="text-4xl mb-2">🧪</div>
              <div className="font-heading font-bold text-lg mb-1">No fine-tune jobs yet</div>
              <p className="text-sm text-slate-600">Launch your first supervised fine-tune above. When it completes, we auto-wrap the endpoint as a Custom Brain.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="ft-jobs-list">
              {jobs.map((j) => <JobRow key={j.job_id} job={j} onDelete={cancel} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function JobDetail({ job, onRegister, registering, onRefresh, onDelete }) {
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState([]);  // {step, loss, ...}
  const [streaming, setStreaming] = useState(false);
  const esRef = useRef(null);

  // Open SSE log stream when job is running
  useEffect(() => {
    if (!job || job.status !== "running") {
      esRef.current?.close?.();
      esRef.current = null;
      setStreaming(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) return;
    const url = `${API}/fine_tune/jobs/${job.job_id}/logs?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;
    setStreaming(true);
    es.onmessage = (msg) => {
      try {
        const ev = JSON.parse(msg.data);
        if (ev.type === "metric" && typeof ev.step === "number" && ev.step >= 0) {
          setMetrics((m) => [...m.slice(-99), ev]);
        }
        if (ev.type === "log" || ev.type === "warn" || ev.type === "status" || ev.type === "snapshot") {
          setLogs((l) => [...l.slice(-59), ev]);
        }
        if (ev.type === "ended") {
          es.close();
          setStreaming(false);
          onRefresh();
        }
      } catch (e) {
        console.warn("Bad SSE frame:", e);
      }
    };
    es.onerror = () => {
      es.close();
      setStreaming(false);
    };
    return () => { es.close(); esRef.current = null; };
  }, [job?.job_id, job?.status, onRefresh]);  // eslint-disable-line react-hooks/exhaustive-deps

  const lastMetric = metrics[metrics.length - 1];

  return (
    <div className="brut-card p-5 space-y-4" data-testid="ft-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <button onClick={() => nav("/studio/fine-tune")} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold mb-2">
            <ArrowLeft size={14} strokeWidth={2.5} /> All jobs
          </button>
          <div className="font-heading font-bold text-2xl">{job.base_model.split("/").pop()}</div>
          <div className="text-xs text-slate-500 font-mono">{job.job_id}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={job.status} />
          {streaming && (
            <span className="text-[10px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 border-2 border-emerald-600 text-emerald-800 inline-flex items-center gap-1" data-testid="ft-live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
          )}
          <button onClick={onRefresh} className="brut-btn text-xs bg-white text-slate-900" data-testid="ft-refresh">
            <RefreshCw size={11} strokeWidth={3} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Info label="Provider" value={job.provider} />
        <Info label="Base model" value={job.base_model} mono />
        <Info label="Provider job ID" value={job.provider_job_id || "—"} mono />
        <Info label="Output model" value={job.output_model_slug || "—"} mono />
        <Info label="Dataset" value={job.dataset?.hf_id || job.dataset?.url || job.dataset?.ref || "—"} mono />
        <Info label="Epochs / LR / LoRA r" value={`${job.hyperparams?.epochs} / ${job.hyperparams?.learning_rate} / ${job.hyperparams?.lora_rank}`} />
      </div>

      {/* Live metrics */}
      {(streaming || metrics.length > 0) && (
        <div className="brut-card p-4 bg-slate-50 space-y-3" data-testid="ft-metrics">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 inline-flex items-center gap-2">
              <BarChart3 size={12} strokeWidth={2.5} /> Training metrics
            </div>
            {lastMetric && (
              <div className="text-xs text-slate-500 font-mono">step <b className="text-slate-900 tabular-nums">{lastMetric.step}</b></div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <MetricCell label="Loss" value={lastMetric?.loss?.toFixed?.(4) ?? "—"} />
            <MetricCell label="Epoch" value={lastMetric?.epoch ?? "—"} />
            <MetricCell label="Tokens" value={lastMetric?.tokens_seen ? new Intl.NumberFormat().format(lastMetric.tokens_seen) : "—"} />
            <MetricCell label="LR" value={lastMetric?.learning_rate?.toExponential?.(2) ?? "—"} />
          </div>
          {metrics.length > 1 && <LossSparkline metrics={metrics} />}
        </div>
      )}

      {/* Log tail */}
      {logs.length > 0 && (
        <details className="brut-card p-3 bg-slate-900 text-emerald-200" open data-testid="ft-log-tail">
          <summary className="cursor-pointer text-xs uppercase tracking-widest font-heading font-bold text-emerald-300 mb-2">
            📜 Provider log ({logs.length})
          </summary>
          <div className="mt-2 space-y-1 font-mono text-[11px] max-h-48 overflow-y-auto">
            {logs.map((l, i) => (
              <div key={`log-${i}`} className={l.type === "warn" ? "text-amber-300" : l.type === "status" ? "text-cyan-300" : "text-emerald-200"}>
                {l.type === "status" ? `→ status: ${l.status}` : l.type === "warn" ? `⚠ ${l.message}` : l.type === "snapshot" ? `▶ opened stream for ${l.provider_job_id || l.job_id}` : l.message}
              </div>
            ))}
          </div>
        </details>
      )}

      {job.error && (
        <div className="text-sm bg-red-50 border-2 border-red-300 text-red-800 rounded-lg p-3" data-testid="ft-error">
          <b>Error:</b> {job.error}
        </div>
      )}

      {job.status === "completed" && !job.brain_id && (
        <button onClick={onRegister} disabled={registering} className="brut-btn text-sm" data-testid="ft-register">
          {registering ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={3} />} Register as Custom Brain
        </button>
      )}
      {job.brain_id && (
        <div className="text-sm bg-green-50 border-2 border-green-300 text-green-800 rounded-lg p-3 inline-flex items-center gap-2" data-testid="ft-registered">
          <Check size={14} strokeWidth={3} /> Registered as <span className="font-mono font-bold">{job.brain_id}</span> — available in Compose &amp; Workflows.
        </div>
      )}

      <div className="pt-2 border-t border-slate-200">
        <button onClick={onDelete} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs font-bold">
          <Trash2 size={12} strokeWidth={2.5} /> Delete job record
        </button>
      </div>
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="border-2 border-slate-300 rounded-lg bg-white p-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-heading font-bold">{label}</div>
      <div className="font-heading font-bold text-lg tabular-nums truncate">{value}</div>
    </div>
  );
}

function LossSparkline({ metrics }) {
  const losses = metrics.filter((m) => typeof m.loss === "number").map((m) => m.loss);
  if (losses.length < 2) return null;
  const min = Math.min(...losses);
  const max = Math.max(...losses);
  const range = max - min || 1;
  const points = losses
    .map((v, i) => {
      const x = (i / (losses.length - 1)) * 300;
      const y = 60 - ((v - min) / range) * 55;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-heading font-bold mb-1">Loss trend (last {losses.length} steps)</div>
      <svg viewBox="0 0 300 60" className="w-full h-16 border-2 border-slate-300 rounded bg-white">
        <polyline fill="none" stroke="#EA580C" strokeWidth="1.5" points={points} />
      </svg>
    </div>
  );
}

function JobRow({ job, onDelete }) {
  return (
    <Link
      to={`/studio/fine-tune/${job.job_id}`}
      className="brut-card p-3 flex items-center justify-between gap-3 hover:-translate-y-0.5 transition-transform"
      data-testid={`ft-job-${job.job_id}`}
    >
      <div className="min-w-0">
        <div className="font-heading font-bold text-sm truncate">{job.base_model.split("/").pop()}</div>
        <div className="text-xs text-slate-500 font-mono">{job.provider} · {job.job_id}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusBadge status={job.status} />
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(job.job_id); }}
          className="text-red-500 hover:text-red-700"
          title="Delete"
        >
          <Trash2 size={13} strokeWidth={2.5} />
        </button>
      </div>
    </Link>
  );
}

function StatusBadge({ status }) {
  const styles = {
    queued:    { bg: "#F1F5F9", fg: "#475569", label: "Queued" },
    running:   { bg: "#FEF3C7", fg: "#92400E", label: "Running" },
    completed: { bg: "#D1FAE5", fg: "#065F46", label: "Completed" },
    failed:    { bg: "#FEE2E2", fg: "#991B1B", label: "Failed" },
  };
  const s = styles[status] || styles.queued;
  return (
    <span
      className="text-[10px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border-2 inline-flex items-center gap-1"
      style={{ backgroundColor: s.bg, color: s.fg, borderColor: s.fg }}
      data-testid={`ft-status-${status}`}
    >
      {status === "running" && <Loader2 size={9} className="animate-spin" />}
      {s.label}
    </span>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : "text-sm"} break-all`}>{value}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
    </div>
  );
}
